import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { cobrandingAssetsService } from '../../../services/marketingCampaignsService';

const campaignTypes = [
  { value: 'event', label: 'Event/Webinar', icon: 'Calendar' },
  { value: 'case-study', label: 'Case Study', icon: 'FileText' },
  { value: 'co-branding', label: 'Co-Branding', icon: 'Palette' },
  { value: 'content', label: 'Content Marketing', icon: 'PenTool' },
  { value: 'social', label: 'Social Media', icon: 'Share2' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'bg-success/10 text-success border-success/20';
    case 'planning': return 'bg-primary/10 text-primary border-primary/20';
    case 'in-review': return 'bg-warning/10 text-warning border-warning/20';
    case 'completed': return 'bg-muted text-muted-foreground border-border';
    case 'approved': return 'bg-success/10 text-success border-success/20';
    case 'pending-review': return 'bg-warning/10 text-warning border-warning/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

// ─── Event Calendar ───────────────────────────────────────────────────────────
const EventCalendar = ({ campaigns }) => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today?.getMonth());
  const [viewYear, setViewYear] = useState(today?.getFullYear());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0)?.getDate();
  const firstDay = new Date(viewYear, viewMonth, 1)?.getDay();
  const monthName = new Date(viewYear, viewMonth)?.toLocaleString('default', { month: 'long' });

  const eventCampaigns = campaigns?.filter(c => {
    if (!c?.start_date && !c?.startDate) return false;
    const start = new Date(c?.start_date || c?.startDate);
    const end = new Date(c?.end_date || c?.endDate || c?.start_date || c?.startDate);
    return (
      (start?.getMonth() === viewMonth && start?.getFullYear() === viewYear) ||
      (end?.getMonth() === viewMonth && end?.getFullYear() === viewYear)
    );
  });

  const getEventsForDay = (day) => {
    return eventCampaigns?.filter(c => {
      const start = new Date(c?.start_date || c?.startDate);
      const end = new Date(c?.end_date || c?.endDate || c?.start_date || c?.startDate);
      let d = new Date(viewYear, viewMonth, day);
      return d >= start && d <= end;
    });
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells?.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells?.push(d);

  return (
    <div>
      {/* Month nav */}
      <div className="flex items-center justify-between mb-16">
        <button
          onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); }}
          className="p-8 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="ChevronLeft" size={18} />
        </button>
        <span className="text-base font-semibold text-foreground">{monthName} {viewYear}</span>
        <button
          onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); }}
          className="p-8 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="ChevronRight" size={18} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-8">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-8">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {cells?.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const events = getEventsForDay(day);
          const isToday = day === today?.getDate() && viewMonth === today?.getMonth() && viewYear === today?.getFullYear();
          return (
            <div
              key={day}
              className={`min-h-[56px] p-4 rounded-lg border transition-colors ${
                isToday ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
              }`}
            >
              <div className={`text-xs font-semibold mb-4 ${isToday ? 'text-primary' : 'text-foreground'}`}>{day}</div>
              {events?.slice(0, 2)?.map((ev, i) => (
                <div key={i} className="text-[10px] leading-tight px-4 py-2 rounded bg-primary/10 text-primary truncate mb-1">
                  {ev?.name}
                </div>
              ))}
              {events?.length > 2 && (
                <div className="text-[10px] text-muted-foreground">+{events?.length - 2} more</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upcoming events list */}
      {eventCampaigns?.length > 0 && (
        <div className="mt-20">
          <h4 className="text-sm font-semibold text-foreground mb-12">Events This Month</h4>
          <div className="space-y-8">
            {eventCampaigns?.map((c, i) => (
              <div key={i} className="flex items-center gap-12 p-12 bg-muted/30 rounded-lg border border-border">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={campaignTypes?.find(t => t?.value === c?.type)?.icon || 'Calendar'} size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{c?.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(c?.start_date || c?.startDate)?.toLocaleDateString()} — {new Date(c?.end_date || c?.endDate || c?.start_date || c?.startDate)?.toLocaleDateString()}
                  </div>
                </div>
                <span className={`px-8 py-4 rounded-md text-xs font-medium border ${getStatusColor(c?.status)}`}>
                  {c?.status?.replace('-', ' ')?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {eventCampaigns?.length === 0 && (
        <div className="text-center py-24 text-muted-foreground">
          <Icon name="Calendar" size={36} className="mx-auto mb-8 opacity-30" />
          <p className="text-sm">No campaign events this month.</p>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const JointMarketingSection = ({ campaigns, onCreateCampaign, onUpdateCampaign, partnerId }) => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: '', type: '', startDate: '', endDate: '', budget: '', status: 'planning' });
  const [creating, setCreating] = useState(false);

  // Co-branding assets state
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAssets();
  }, [partnerId]);

  const loadAssets = async () => {
    setLoadingAssets(true);
    try {
      const data = await cobrandingAssetsService?.getAll(partnerId);
      setAssets(data || []);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const saved = await cobrandingAssetsService?.uploadAsset(file, partnerId);
      setAssets(prev => [saved, ...prev]);
    } catch (err) {
      setUploadError(err?.message || 'Upload failed. Ensure the partner-assets storage bucket exists.');
    } finally {
      setUploading(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAsset = async (asset) => {
    try {
      await cobrandingAssetsService?.delete(asset?.id, asset?.storage_path);
      setAssets(prev => prev?.filter(a => a?.id !== asset?.id));
    } catch (err) {
      console.error('Failed to delete asset:', err);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign?.name || !newCampaign?.type) return;
    setCreating(true);
    try {
      await onCreateCampaign?.(newCampaign);
      setShowCreateModal(false);
      setNewCampaign({ name: '', type: '', startDate: '', endDate: '', budget: '', status: 'planning' });
    } finally {
      setCreating(false);
    }
  };

  const tabs = [
    { id: 'campaigns', label: 'Campaigns', icon: 'Megaphone', count: campaigns?.length },
    { id: 'assets', label: 'Co-Branding Assets', icon: 'Palette', count: assets?.length },
    { id: 'calendar', label: 'Event Calendar', icon: 'Calendar', count: campaigns?.filter(c => c?.type === 'event' || c?.start_date || c?.startDate)?.length },
  ];

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-border mb-20 overflow-x-auto">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-8 px-12 py-12 border-b-2 transition-smooth whitespace-nowrap ${
              activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={18} />
            <span className="text-sm font-medium">{tab?.label}</span>
            {tab?.count > 0 && (
              <span className="px-6 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">{tab?.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          <div className="flex items-center justify-between mb-16">
            <span className="text-sm text-muted-foreground">{campaigns?.length} campaign{campaigns?.length !== 1 ? 's' : ''}</span>
            <Button variant="default" iconName="Plus" iconPosition="left" iconSize={16} onClick={() => setShowCreateModal(true)}>
              Create Campaign
            </Button>
          </div>
          {campaigns?.length === 0 ? (
            <div className="text-center py-32 text-muted-foreground">
              <Icon name="Megaphone" size={40} className="mx-auto mb-12 opacity-30" />
              <p className="text-sm">No campaigns yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="space-y-16">
              {campaigns?.map((campaign) => (
                <div key={campaign?.id} className="p-16 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-smooth">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-16">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-12 mb-12">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={campaignTypes?.find(t => t?.value === campaign?.type)?.icon || 'Megaphone'} size={20} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground mb-4">{campaign?.name}</h3>
                          <div className="flex flex-wrap items-center gap-8">
                            <span className={`px-8 py-4 rounded-md text-xs font-medium border ${getStatusColor(campaign?.status)}`}>
                              {campaign?.status?.replace('-', ' ')?.toUpperCase()}
                            </span>
                            <span className="text-xs text-muted-foreground caption">
                              {campaignTypes?.find(t => t?.value === campaign?.type)?.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-12">
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">Start Date</div>
                          <div className="text-sm font-medium text-foreground">
                            {campaign?.start_date || campaign?.startDate ? new Date(campaign?.start_date || campaign?.startDate)?.toLocaleDateString() : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">End Date</div>
                          <div className="text-sm font-medium text-foreground">
                            {campaign?.end_date || campaign?.endDate ? new Date(campaign?.end_date || campaign?.endDate)?.toLocaleDateString() : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">Budget</div>
                          <div className="text-sm font-medium text-foreground data-text">${(campaign?.budget || 0)?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground caption mb-4">Leads</div>
                          <div className="text-sm font-medium text-success data-text">{campaign?.leads || 0}</div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-8">
                          <span className="text-xs text-muted-foreground caption">Progress</span>
                          <span className="text-xs font-semibold text-primary data-text">{campaign?.progress || 0}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary transition-smooth" style={{ width: `${campaign?.progress || 0}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex lg:flex-col gap-8">
                      <Button variant="outline" size="sm" iconName="Eye" iconSize={16}>View</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        iconSize={16}
                        onClick={() => onUpdateCampaign?.(campaign?.id, { status: campaign?.status === 'planning' ? 'active' : 'completed' })}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assets Tab */}
      {activeTab === 'assets' && (
        <div>
          {/* Upload zone */}
          <div
            className="border-2 border-dashed border-border rounded-xl p-24 text-center mb-20 hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => fileInputRef?.current?.click()}
          >
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.pptx,.docx,.svg,.zip" />
            {uploading ? (
              <div className="flex items-center justify-center gap-12">
                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Uploading...</span>
              </div>
            ) : (
              <>
                <Icon name="Upload" size={32} className="mx-auto mb-8 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground mb-4">Upload Co-Branding Asset</p>
                <p className="text-xs text-muted-foreground">Images, PDFs, PPTX, SVG, ZIP — click to browse</p>
              </>
            )}
          </div>
          {uploadError && (
            <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm mb-16">
              <Icon name="AlertCircle" size={16} />{uploadError}
            </div>
          )}

          {loadingAssets ? (
            <div className="text-center py-16 text-muted-foreground text-sm">Loading assets...</div>
          ) : assets?.length === 0 ? (
            <div className="text-center py-24 text-muted-foreground">
              <Icon name="Palette" size={36} className="mx-auto mb-8 opacity-30" />
              <p className="text-sm">No assets uploaded yet.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {assets?.map((asset) => (
                <div key={asset?.id} className="flex items-center justify-between p-16 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-smooth">
                  <div className="flex items-center gap-12 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground mb-4">{asset?.name}</h4>
                      <div className="flex flex-wrap items-center gap-8">
                        <span className="text-xs text-muted-foreground caption">{asset?.format}</span>
                        <span className={`px-8 py-4 rounded-md text-xs font-medium border ${getStatusColor(asset?.status)}`}>
                          {asset?.status?.replace('-', ' ')?.toUpperCase()}
                        </span>
                        <span className="text-xs text-muted-foreground caption">
                          {new Date(asset?.created_at || asset?.uploadDate)?.toLocaleDateString()}
                        </span>
                        {asset?.file_size > 0 && (
                          <span className="text-xs text-muted-foreground caption">
                            {(asset?.file_size / 1024)?.toFixed(0)} KB
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-8">
                    <Button variant="ghost" size="icon" iconName="Download" iconSize={18} />
                    <Button variant="ghost" size="icon" iconName="Trash2" iconSize={18} onClick={() => handleDeleteAsset(asset)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <EventCalendar campaigns={campaigns} />
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-24 py-20 border-b border-slate-100 flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-900">Create Marketing Campaign</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-24 space-y-16 overflow-y-auto flex-1">
              <Input
                label="Campaign Name"
                type="text"
                placeholder="Enter campaign name"
                value={newCampaign?.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e?.target?.value })}
                required
              />
              <Select
                label="Campaign Type"
                options={campaignTypes?.map(t => ({ value: t?.value, label: t?.label }))}
                value={newCampaign?.type}
                onChange={(value) => setNewCampaign({ ...newCampaign, type: value })}
                placeholder="Select campaign type"
                required
              />
              <div className="grid grid-cols-2 gap-16">
                <Input label="Start Date" type="date" value={newCampaign?.startDate} onChange={(e) => setNewCampaign({ ...newCampaign, startDate: e?.target?.value })} required />
                <Input label="End Date" type="date" value={newCampaign?.endDate} onChange={(e) => setNewCampaign({ ...newCampaign, endDate: e?.target?.value })} required />
              </div>
              <Input
                label="Budget (OMR)"
                type="number"
                placeholder="Enter budget amount"
                value={newCampaign?.budget}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget: e?.target?.value })}
              />
            </div>
            <div className="flex items-center gap-12 px-24 py-16 border-t border-slate-100 flex-shrink-0">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button
                onClick={handleCreateCampaign}
                disabled={!newCampaign?.name || !newCampaign?.type || creating}
                className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8"
              >
                {creating ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Creating...</> : <><Icon name="Plus" size={16} /> Create Campaign</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JointMarketingSection;